import type { GardenCodeData, MascotId, MascotSavedState, SaveData } from "../types";

export const CURRENT_SCHEMA_VERSION = 2;
export const SAVE_KEY = "gardenMeister.save";
export const BACKUP_KEY = "gardenMeister.save.backup";
/** 同期の衝突で採用されなかった側を退避しておくスロット。自動では消さない。 */
export const CONFLICT_KEY = "gardenMeister.save.conflict";
const GARDEN_CODE_PREFIX = "GM1-";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface LoadResult {
  data: SaveData | null;
  source: "primary" | "backup" | "none";
  error?: string;
  /**
   * 現在のビルドより新しいスキーマのセーブを検出した場合に true。
   * ロールバック配信で古いクライアントが新しいセーブを踏んだ状況なので、
   * 新規ゲームで黙って上書きせず、書き込み自体を禁止する。
   */
  locked?: boolean;
}

export type SaveOutcome =
  | { ok: true; data: SaveData }
  | { ok: false; reason: "locked" | "quota" | "unavailable"; message: string };

export class GardenStorage {
  /** 将来バージョンのセーブを検出したら true。以降の書き込みをすべて拒否する。 */
  private locked = false;

  constructor(private readonly storage: StorageLike = window.localStorage) {}

  get isLocked(): boolean {
    return this.locked;
  }

  save(data: SaveData): SaveOutcome {
    if (this.locked) return { ok: false, reason: "locked", message: "新しいバージョンのセーブデータを検出したため、保存を停止しています。" };
    const prepared = this.withChecksum({ ...data, schemaVersion: CURRENT_SCHEMA_VERSION, updatedAt: Date.now() });
    try {
      const previous = this.storage.getItem(SAVE_KEY);
      if (previous !== null) this.storage.setItem(BACKUP_KEY, previous);
      this.storage.setItem(SAVE_KEY, JSON.stringify(prepared));
      return { ok: true, data: prepared };
    } catch (error) {
      // 容量超過やプライベートモードでの書き込み拒否。ここで握り潰さないとゲームループごと止まる。
      const quota = error instanceof DOMException && (error.name === "QuotaExceededError" || error.name === "NS_ERROR_DOM_QUOTA_REACHED");
      return quota
        ? { ok: false, reason: "quota", message: "ブラウザの保存容量が上限に達しました。" }
        : { ok: false, reason: "unavailable", message: "このブラウザではローカル保存を利用できません。" };
    }
  }

  load(): LoadResult {
    const primary = this.readSlot(SAVE_KEY);
    const backup: { data: SaveData | null; error?: string; futureVersion?: boolean } = primary.data ? { data: null } : this.readSlot(BACKUP_KEY);
    if (primary.futureVersion || backup.futureVersion) this.locked = true;
    const lock = this.locked ? { locked: true as const } : {};
    if (primary.data) return { data: primary.data, source: "primary", ...lock };
    if (backup.data) return { data: backup.data, source: "backup", ...(primary.error ? { error: primary.error } : {}), ...lock };
    const error = [primary.error, backup.error].filter(Boolean).join(" / ");
    return { data: null, source: "none", ...(error ? { error } : {}), ...lock };
  }

  /** 同期で破棄される側のセーブを退避する。失敗しても呼び出し側の処理は続行させる。 */
  stashConflict(data: SaveData): void {
    try {
      this.storage.setItem(CONFLICT_KEY, JSON.stringify(data));
    } catch {
      // 退避できなくても本流の同期は止めない。
    }
  }

  readConflict(): SaveData | null {
    return this.readSlot(CONFLICT_KEY).data;
  }

  clear(): void {
    this.storage.removeItem(SAVE_KEY);
    this.storage.removeItem(BACKUP_KEY);
  }

  exportGardenCode(save: SaveData): string {
    const layout: GardenCodeData = {
      format: "garden-meister-layout",
      version: 1,
      gardenId: save.gardenId,
      gridWidth: save.gridWidth,
      gridHeight: save.gridHeight,
      terrain: save.grid.map(({ x, y, terrain, elevation }) => ({ x, y, terrain, elevation })),
      plants: save.plants.filter((plant) => plant.stage !== "withered").map(({ speciesId, gridPosition }) => ({ speciesId, gridPosition: { ...gridPosition } })),
      decorations: save.inventory.decorations.map(({ decorationId, gridPosition, rotation }) => ({ decorationId, gridPosition: { ...gridPosition }, rotation })),
    };
    return GARDEN_CODE_PREFIX + encodeBase64Url(JSON.stringify(layout));
  }

  importGardenCode(code: string): GardenCodeData {
    const normalized = code.trim();
    if (!normalized.startsWith(GARDEN_CODE_PREFIX)) throw new Error("庭コードの形式が正しくありません。");
    let parsed: unknown;
    try {
      parsed = JSON.parse(decodeBase64Url(normalized.slice(GARDEN_CODE_PREFIX.length)));
    } catch {
      throw new Error("庭コードを読み取れませんでした。");
    }
    if (!isGardenCodeData(parsed)) throw new Error("庭コードのデータが不正です。");
    return parsed;
  }

  private readSlot(key: string): { data: SaveData | null; error?: string; futureVersion?: boolean } {
    let raw: string | null = null;
    try {
      raw = this.storage.getItem(key);
    } catch {
      return { data: null, error: `${key}: ローカル保存を読み取れませんでした。` };
    }
    if (raw === null) return { data: null };
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!isSaveData(parsed)) return { data: null, error: `${key}: セーブデータの構造が不正です。` };
      if (parsed.schemaVersion > CURRENT_SCHEMA_VERSION) return { data: null, error: `${key}: 新しいバージョンのセーブデータです。`, futureVersion: true };
      if (parsed.checksum !== checksumFor(parsed)) return { data: null, error: `${key}: チェックサムが一致しません。` };
      return { data: this.migrate(parsed) };
    } catch (error) {
      return { data: null, error: `${key}: ${error instanceof Error ? error.message : "読み込みに失敗しました。"}` };
    }
  }

  private migrate(data: SaveData): SaveData {
    if (data.schemaVersion < 1) throw new Error(`スキーマバージョン${data.schemaVersion}の移行処理がありません。`);
    const legacy = data as SaveData & { mascotState?: Partial<MascotSavedState> };
    const gnome = "gnome_default" as MascotId;
    const defaultStatus: MascotSavedState = {
      level: 1,
      experience: 0,
      affinity: 0,
      position: legacy.mascotState?.position ? { ...legacy.mascotState.position } : { x: 0, y: 0 },
      currentAction: legacy.mascotState?.currentAction ?? "idle",
      currentStamina: legacy.mascotState?.currentStamina ?? 100,
    };
    const unlocked = [...new Set((data.unlockedMascots?.length ? data.unlockedMascots : [gnome]).filter((id): id is MascotId => typeof id === "string"))];
    const maxMascotSlots = Math.max(1, Math.min(5, Math.floor(data.maxMascotSlots ?? 1)));
    const active = [...new Set((data.activeMascotIds?.length ? data.activeMascotIds : [gnome]).filter((id): id is MascotId => unlocked.includes(id)))].slice(0, maxMascotSlots);
    const migrated: SaveData = {
      ...data,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      unlockedMascots: unlocked,
      activeMascotIds: active,
      maxMascotSlots,
      mascotStatus: { [gnome]: defaultStatus, ...(data.mascotStatus ?? {}) },
      saveVersion: "3.1",
      checksum: "",
    };
    return this.withChecksum(migrated);
  }

  private withChecksum(data: SaveData): SaveData {
    return { ...data, checksum: checksumFor(data) };
  }
}

/**
 * セーブにチェックサムを付け直す。
 * サーバー上の庭を書き換える（休止設定の切り替えなど）ときに、
 * 中身とチェックサムが食い違ったまま保存されないよう外からも使えるようにしている。
 */
export function withChecksum(data: SaveData): SaveData {
  return { ...data, checksum: checksumFor(data) };
}

function checksumFor(data: SaveData): string {
  const canonical = stableStringify({ ...data, checksum: "" });
  let hash = 0x811c9dc5;
  for (let index = 0; index < canonical.length; index += 1) {
    hash ^= canonical.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`).join(",")}}`;
}

function isSaveData(value: unknown): value is SaveData {
  if (!isRecord(value)) return false;
  return Number.isInteger(value.schemaVersion) && typeof value.gardenId === "string" && isFiniteNumber(value.createdAt) && isFiniteNumber(value.updatedAt) && Number.isInteger(value.gridWidth) && Number.isInteger(value.gridHeight) && Array.isArray(value.grid) && Array.isArray(value.plants) && Array.isArray(value.quests) && isRecord(value.time) && isRecord(value.weather) && isRecord(value.inventory) && isRecord(value.settings) && typeof value.checksum === "string";
}

function isGardenCodeData(value: unknown): value is GardenCodeData {
  if (!isRecord(value) || value.format !== "garden-meister-layout" || value.version !== 1 || typeof value.gardenId !== "string" || !Number.isInteger(value.gridWidth) || !Number.isInteger(value.gridHeight) || !Array.isArray(value.terrain) || !Array.isArray(value.plants) || !Array.isArray(value.decorations)) return false;
  const width = value.gridWidth as number;
  const height = value.gridHeight as number;
  if (width < 1 || width > 24 || height < 1 || height > 24) return false;
  const validPosition = (candidate: unknown): boolean => isRecord(candidate) && Number.isInteger(candidate.x) && Number.isInteger(candidate.y) && (candidate.x as number) >= 0 && (candidate.y as number) >= 0 && (candidate.x as number) < width && (candidate.y as number) < height;
  const terrains = new Set(["soil", "grass", "pavingStone", "water", "path"]);
  return value.terrain.every((cell) => isRecord(cell) && validPosition(cell) && terrains.has(String(cell.terrain)) && isFiniteNumber(cell.elevation)) && value.plants.every((plant) => isRecord(plant) && typeof plant.speciesId === "string" && validPosition(plant.gridPosition)) && value.decorations.every((decoration) => isRecord(decoration) && typeof decoration.decorationId === "string" && validPosition(decoration.gridPosition) && [0, 90, 180, 270].includes(Number(decoration.rotation)));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function encodeBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function decodeBase64Url(value: string): string {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
